import { supabase } from '../../lib/supabase';

export type CustomerCreditMovementType = 'credit' | 'debit';

export interface CustomerCreditUser {
  id: number;
  username: string;
  fullname?: string | null;
  avatar?: string | null;
  email_copy?: string | null;
}

export interface StoreCustomerCredit {
  id: number;
  store_id: number;
  user_id: number;
  balance: number;
  active: boolean;
  created_at: string;
  updated_at: string;
  user?: CustomerCreditUser | null;
}

export interface StoreCustomerCreditMovement {
  id: number;
  credit_account_id: number;
  movement_type: CustomerCreditMovementType;
  amount: number;
  reason: string;
  created_by: number | null;
  created_at: string;
  creator?: {
    id: number;
    username?: string;
    fullname?: string;
  } | null;
  credit_account?: {
    id: number;
    store_id: number;
    user_id: number;
    user?: CustomerCreditUser | null;
  } | null;
}

export interface CustomerCreditStats {
  totalCustomersWithCredit: number;
  totalCreditCirculating: number;
  averageCreditPerCustomer: number;
  activeAccountsCount: number;
}

/**
 * Busca usuários pelo username ou nome utilizando a RPC segura `get_safe_users`
 * para abrir ou vincular ficha de crédito de clientes.
 */
export const searchUsersForCredit = async (searchTerm: string): Promise<CustomerCreditUser[]> => {
  if (!searchTerm || searchTerm.trim().length < 2) return [];
  try {
    const raw = searchTerm.trim();
    const clean = raw.replace(/^@/, '').trim();
    if (!clean) return [];

    // Se for numérico, tenta buscar diretamente pelo ID primeiro
    const isNumeric = !isNaN(Number(clean)) && !clean.includes(' ') && !clean.includes('@');
    if (isNumeric) {
      const { data: byId } = await (supabase.rpc('get_safe_users') as any)
        .select('id, username, fullname, avatar')
        .eq('id', Number(clean))
        .limit(1);
      if (byId && byId.length > 0) {
        return byId as CustomerCreditUser[];
      }
    }

    // Busca clientes via get_safe_users RPC
    const { data: safeUsers, error: safeErr } = await (supabase.rpc('get_safe_users') as any)
      .select('id, username, fullname, avatar')
      .or(`username.ilike.%${clean}%,fullname.ilike.%${clean}%`)
      .order('username', { ascending: true })
      .limit(15);

    if (safeErr) {
      console.warn('Erro ao buscar clientes via get_safe_users RPC, tentando fallback:', safeErr);
      const { data: fallbackData } = await supabase
        .from('users')
        .select('id, username, fullname, avatar')
        .or(`username.ilike.%${clean}%,fullname.ilike.%${clean}%`)
        .limit(15);
      return (fallbackData || []) as CustomerCreditUser[];
    }

    return (safeUsers || []) as CustomerCreditUser[];
  } catch (error) {
    console.error('Error searching users for credit with get_safe_users:', error);
    return [];
  }
};

/**
 * Busca fichas de crédito dos clientes de uma loja e hidrata os clientes com get_safe_users
 */
export const getStoreCustomerCredits = async (
  storeId: number,
  options?: {
    search?: string;
    filter?: 'all' | 'positive_only' | 'zero_only' | 'inactive';
  }
): Promise<StoreCustomerCredit[]> => {
  try {
    let query = supabase
      .from('store_customer_credits')
      .select('*')
      .eq('store_id', storeId)
      .order('balance', { ascending: false });

    if (options?.filter === 'inactive') {
      query = query.eq('active', false);
    } else if (options?.filter === 'positive_only') {
      query = query.eq('active', true).gt('balance', 0);
    } else if (options?.filter === 'zero_only') {
      query = query.eq('active', true).eq('balance', 0);
    }

    const { data, error } = await query;
    if (error) throw error;

    let items = (data || []).map((item: any) => ({
      ...item,
      balance: Number(item.balance) || 0,
      user: null
    })) as StoreCustomerCredit[];

    // Usar a RPC get_safe_users para obter os dados seguros de todos os clientes vinculados
    const userIds = Array.from(new Set(items.map(i => i.user_id).filter(Boolean)));
    if (userIds.length > 0) {
      try {
        const { data: safeUsers, error: safeErr } = await (supabase.rpc('get_safe_users') as any)
          .select('id, username, fullname, avatar')
          .in('id', userIds);

        if (!safeErr && safeUsers && safeUsers.length > 0) {
          const userMap = new Map<number, CustomerCreditUser>(
            safeUsers.map((u: any) => [u.id, u as CustomerCreditUser])
          );
          items = items.map(item => ({
            ...item,
            user: userMap.get(item.user_id) || null
          }));
        }
      } catch (safeErr) {
        console.warn('Erro ao carregar dados dos clientes via get_safe_users:', safeErr);
      }
    }

    // Filtro de busca textual (por username ou nome completo)
    if (options?.search && options.search.trim()) {
      const q = options.search.trim().toLowerCase().replace(/^@/, '');
      items = items.filter(item => {
        const u = item.user;
        if (!u) return false;
        return (
          (u.username && u.username.toLowerCase().includes(q)) ||
          (u.fullname && u.fullname.toLowerCase().includes(q)) ||
          (u.email_copy && u.email_copy.toLowerCase().includes(q))
        );
      });
    }

    return items;
  } catch (error) {
    console.error('Error fetching store customer credits:', error);
    throw error;
  }
};

/**
 * Obtém ou cria uma conta de crédito para um cliente específico na loja
 */
export const getOrCreateCustomerCreditAccount = async (
  storeId: number,
  userId: number
): Promise<StoreCustomerCredit> => {
  try {
    // 1. Busca dados seguros do cliente via get_safe_users RPC
    let user: CustomerCreditUser | null = null;
    try {
      const { data: safeUser } = await (supabase.rpc('get_safe_users') as any)
        .select('id, username, fullname, avatar')
        .eq('id', userId)
        .maybeSingle();
      if (safeUser) {
        user = safeUser as CustomerCreditUser;
      }
    } catch (e) {
      console.warn('Erro ao buscar usuário via get_safe_users:', e);
    }

    // 2. Verifica se já existe a conta
    const { data: existing, error: findError } = await supabase
      .from('store_customer_credits')
      .select('*')
      .eq('store_id', storeId)
      .eq('user_id', userId)
      .maybeSingle();

    if (findError) throw findError;

    if (existing) {
      if (!existing.active) {
        // Reativa se estiver inativa
        const { data: reactivated, error: reactivateErr } = await supabase
          .from('store_customer_credits')
          .update({ active: true, updated_at: new Date().toISOString() })
          .eq('id', existing.id)
          .select('*')
          .single();

        if (reactivateErr) throw reactivateErr;
        return {
          ...reactivated,
          balance: Number(reactivated.balance) || 0,
          user
        };
      }

      return {
        ...existing,
        balance: Number(existing.balance) || 0,
        user
      };
    }

    // 3. Cria nova conta de crédito
    const { data: created, error: createError } = await supabase
      .from('store_customer_credits')
      .insert({
        store_id: storeId,
        user_id: userId,
        balance: 0,
        active: true
      })
      .select('*')
      .single();

    if (createError) throw createError;

    return {
      ...created,
      balance: Number(created.balance) || 0,
      user
    };
  } catch (error) {
    console.error('Error in getOrCreateCustomerCreditAccount:', error);
    throw error;
  }
};

/**
 * Registra movimentação de crédito ou débito utilizando a RPC `register_customer_credit_movement`
 */
export const registerCustomerCreditMovement = async (payload: {
  creditAccountId: number;
  movementType: CustomerCreditMovementType;
  amount: number;
  reason: string;
}): Promise<StoreCustomerCredit> => {
  try {
    const { data, error } = await supabase.rpc('register_customer_credit_movement', {
      p_credit_account_id: payload.creditAccountId,
      p_movement_type: payload.movementType,
      p_amount: payload.amount,
      p_reason: payload.reason.trim()
    });

    if (error) {
      console.error('Error in register_customer_credit_movement RPC:', error);
      throw error;
    }

    // Busca dados atualizados da conta
    const { data: updatedAccount, error: fetchErr } = await supabase
      .from('store_customer_credits')
      .select('*')
      .eq('id', payload.creditAccountId)
      .single();

    if (fetchErr || !updatedAccount) {
      return {
        ...data,
        balance: Number(data.balance) || 0
      };
    }

    // Carrega dados seguros do cliente via get_safe_users RPC
    let user: CustomerCreditUser | null = null;
    if (updatedAccount.user_id) {
      try {
        const { data: safeUser } = await (supabase.rpc('get_safe_users') as any)
          .select('id, username, fullname, avatar')
          .eq('id', updatedAccount.user_id)
          .maybeSingle();
        user = safeUser || null;
      } catch (e) {
        console.warn('Erro ao buscar cliente via get_safe_users em registerCustomerCreditMovement:', e);
      }
    }

    return {
      ...updatedAccount,
      balance: Number(updatedAccount.balance) || 0,
      user
    };
  } catch (error) {
    console.error('Error registering customer credit movement:', error);
    throw error;
  }
};

/**
 * Busca histórico de movimentações de uma conta de crédito específica
 */
export const getCustomerCreditMovements = async (
  creditAccountId: number,
  limit = 50
): Promise<StoreCustomerCreditMovement[]> => {
  try {
    const { data, error } = await supabase
      .from('store_customer_credit_movements')
      .select('*')
      .eq('credit_account_id', creditAccountId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;

    let items = (data || []).map((m: any) => ({
      ...m,
      amount: Number(m.amount) || 0,
      creator: null
    }));

    // Hidrata os operadores que criaram as movimentações via get_safe_users
    const creatorIds = Array.from(new Set(items.map((m: any) => m.created_by).filter(Boolean)));
    if (creatorIds.length > 0) {
      try {
        const { data: safeCreators } = await (supabase.rpc('get_safe_users') as any)
          .select('id, username, fullname')
          .in('id', creatorIds);

        if (safeCreators && safeCreators.length > 0) {
          const creatorMap = new Map(safeCreators.map((c: any) => [c.id, c]));
          items = items.map(m => ({
            ...m,
            creator: m.created_by ? creatorMap.get(m.created_by) || null : null
          }));
        }
      } catch (e) {
        console.warn('Erro ao carregar criadores via get_safe_users:', e);
      }
    }

    return items;
  } catch (error) {
    console.error('Error fetching customer credit movements:', error);
    throw error;
  }
};

/**
 * Busca as movimentações recentes de crédito de toda a loja (extrato geral de auditoria)
 */
export const getStoreRecentCreditMovements = async (
  storeId: number,
  limit = 50
): Promise<StoreCustomerCreditMovement[]> => {
  try {
    const { data, error } = await supabase
      .from('store_customer_credit_movements')
      .select(`
        *,
        credit_account:store_customer_credits!inner(
          id,
          store_id,
          user_id
        )
      `)
      .eq('credit_account.store_id', storeId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;

    let movements = (data || []).map((m: any) => ({
      ...m,
      amount: Number(m.amount) || 0,
      credit_account: m.credit_account || null,
      creator: null
    }));

    // Hidrata clientes e criadores via get_safe_users RPC
    const clientUserIds = movements.map((m: any) => m.credit_account?.user_id).filter(Boolean);
    const creatorIds = movements.map((m: any) => m.created_by).filter(Boolean);
    const allUserIds = Array.from(new Set([...clientUserIds, ...creatorIds]));

    if (allUserIds.length > 0) {
      try {
        const { data: safeUsers } = await (supabase.rpc('get_safe_users') as any)
          .select('id, username, fullname, avatar')
          .in('id', allUserIds);

        if (safeUsers && safeUsers.length > 0) {
          const userMap = new Map(safeUsers.map((u: any) => [u.id, u]));
          movements = movements.map(m => {
            const clientUser = m.credit_account?.user_id ? userMap.get(m.credit_account.user_id) : null;
            const creatorUser = m.created_by ? userMap.get(m.created_by) : null;
            return {
              ...m,
              creator: creatorUser || null,
              credit_account: m.credit_account
                ? {
                    ...m.credit_account,
                    user: clientUser || null
                  }
                : null
            };
          });
        }
      } catch (e) {
        console.warn('Erro ao hidratar usuários com get_safe_users:', e);
      }
    }

    return movements;
  } catch (error) {
    console.error('Error fetching store recent credit movements:', error);
    return [];
  }
};

/**
 * Altera status ativo/inativo de uma conta de crédito
 */
export const toggleCustomerCreditAccountActive = async (
  creditAccountId: number,
  active: boolean
): Promise<void> => {
  try {
    const { error } = await supabase
      .from('store_customer_credits')
      .update({ active, updated_at: new Date().toISOString() })
      .eq('id', creditAccountId);

    if (error) throw error;
  } catch (error) {
    console.error('Error toggling customer credit active:', error);
    throw error;
  }
};
